import React, { useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';

import Appointment from 'files/Appointment.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * CalendlyScheduler
 *
 * The dealer's own Calendly event, embedded where our calendar would be.
 * Calendly asks for the visitor's name, email, phone and address itself, so
 * the forms send people here without asking for any of it first. Anything we
 * do already know, such as the quiz answers, is passed through so Calendly's
 * form arrives filled in, and the custom answers the dealer mapped carry it
 * into the questions Global Data turns into the lead.
 *
 * The iframe has no edge of its own and its white ground runs straight into
 * the page, so it sits inside a bordered card the same shape as the rest of
 * the site's surfaces. Calendly's event details and landing page are hidden
 * because the page heading above already says what this is.
 */

const MIN_HEIGHT = 700;

// empty strings still land in Calendly's url, so only send what we have
const value = text => (typeof text === 'string' && text.trim()) || undefined;

const CalendlyScheduler = ({ config, customer = {}, form, notes, onScheduled }) => {

    const mobile = Utils.isMobile();

    // Calendly reports its content height as it moves between the calendar,
    // the times and its form, and the embed grows to match. Calendly's docs
    // call for at least 700px, and a report smaller than that collapsed the
    // card to a sliver, so nothing below it is taken.
    const [height, setHeight] = useState(mobile ? 760 : MIN_HEIGHT);

    useCalendlyEventListener({
        onEventScheduled: event => onScheduled(event?.data?.payload),
        onPageHeightResize: event => {
            let next = parseInt(event?.data?.payload?.height, 10);
            if(next > 0) {
                setHeight(Math.max(MIN_HEIGHT, next));
            }
        }
    });

    const name = [customer.firstName, customer.lastName].map(value).filter(Boolean).join(' ');

    return (
        <div style={{
            backgroundColor: Theme.colors.white,
            border: `1px solid ${Theme.colors.border()}`,
            borderRadius: 20,
            marginBottom: 12,
            minHeight: MIN_HEIGHT,
            overflow: 'hidden',
            width: '100%'
        }}>
            <InlineWidget
            iframeTitle={'Book your free evaluation'}
            pageSettings={{
                backgroundColor: 'ffffff',

                // the site asks for cookie consent itself, and a second
                // banner inside the iframe covered the calendar
                hideGdprBanner: true,
                hideEventTypeDetails: true,
                hideLandingPageDetails: true,
                primaryColor: Theme.colors.primary().replace('#', ''),
                textColor: Theme.colors.text().replace('#', '')
            }}
            prefill={{
                customAnswers: Appointment.getCalendlyCustomAnswers(config, {
                    location: value(customer.address?.formatted_address),
                    notes: value(notes),
                    phone_number: value(customer.phone)
                }),
                email: value(customer.email),
                firstName: value(customer.firstName),
                lastName: value(customer.lastName),

                // only used when the event's location is set to the invitee's address
                location: value(customer.address?.formatted_address),
                name: name || undefined
            }}
            styles={{ height, minHeight: MIN_HEIGHT, minWidth: 0, width: '100%' }}
            url={config.url}
            utm={{
                utmCampaign: 'evaluation_booking',
                utmMedium: form || 'website',
                utmSource: 'replicated_website'
            }} />
        </div>
    )
}

export default CalendlyScheduler;
